import type { Doc, Issue, Project } from "./types";
import { humanOwnerId } from "./taskOwnership";

const CLOSED_STATUSES = new Set(["done", "canceled"]);
const ACTIVE_PROJECT_STATUSES = new Set(["planned", "in_progress", "paused"]);

export interface HomeAttentionSummary {
  assigned: number;
  awaitingReview: number;
}

function priorityRank(priority: Issue["priority"]) {
  return priority === 0 ? 5 : priority;
}

export function homeAttentionSummary(
  issues: Issue[],
  currentUserId: string | null
): HomeAttentionSummary {
  if (!currentUserId) return { assigned: 0, awaitingReview: 0 };

  return issues.reduce<HomeAttentionSummary>(
    (summary, issue) => {
      if (CLOSED_STATUSES.has(issue.status)) return summary;
      const awaitingReview =
        issue.requesterId === currentUserId && issue.status === "in_review";
      if (awaitingReview) {
        summary.awaitingReview += 1;
      } else if (humanOwnerId(issue) === currentUserId) {
        summary.assigned += 1;
      }
      return summary;
    },
    { assigned: 0, awaitingReview: 0 }
  );
}

export function relevantHomeTasks(
  issues: Issue[],
  currentUserId: string | null,
  now = Date.now()
) {
  const open = issues.filter((issue) => !CLOSED_STATUSES.has(issue.status));
  const personal = currentUserId
    ? open.filter(
        (issue) =>
          humanOwnerId(issue) === currentUserId ||
          (issue.requesterId === currentUserId && issue.status === "in_review")
      )
    : [];
  const candidates = currentUserId ? personal : open;

  return [...candidates].sort((a, b) => {
    const aAwaiting =
      currentUserId && a.requesterId === currentUserId && a.status === "in_review" ? 0 : 1;
    const bAwaiting =
      currentUserId && b.requesterId === currentUserId && b.status === "in_review" ? 0 : 1;
    if (aAwaiting !== bAwaiting) return aAwaiting - bAwaiting;

    const aOverdue = a.dueDate && a.dueDate < now ? 0 : 1;
    const bOverdue = b.dueDate && b.dueDate < now ? 0 : 1;
    if (aOverdue !== bOverdue) return aOverdue - bOverdue;

    const aDue = a.dueDate ?? Number.POSITIVE_INFINITY;
    const bDue = b.dueDate ?? Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;

    const priorityDelta = priorityRank(a.priority) - priorityRank(b.priority);
    if (priorityDelta !== 0) return priorityDelta;
    return b.updatedAt - a.updatedAt;
  });
}

export function relevantHomeProjects(
  projects: Project[],
  issues: Issue[],
  currentUserId: string | null
) {
  const relevantProjectIds = new Set(
    issues
      .filter(
        (issue) =>
          !CLOSED_STATUSES.has(issue.status) &&
          currentUserId != null &&
          (humanOwnerId(issue) === currentUserId ||
            issue.requesterId === currentUserId)
      )
      .map((issue) => issue.projectId)
      .filter((id): id is string => Boolean(id))
  );

  return projects
    .filter((project) => ACTIVE_PROJECT_STATUSES.has(project.status))
    .sort((a, b) => {
      const aRelevant =
        (currentUserId != null && a.leadId === currentUserId) || relevantProjectIds.has(a.id)
          ? 0
          : 1;
      const bRelevant =
        (currentUserId != null && b.leadId === currentUserId) || relevantProjectIds.has(b.id)
          ? 0
          : 1;
      if (aRelevant !== bRelevant) return aRelevant - bRelevant;
      const aTarget = a.targetDate ?? Number.POSITIVE_INFINITY;
      const bTarget = b.targetDate ?? Number.POSITIVE_INFINITY;
      if (aTarget !== bTarget) return aTarget - bTarget;
      return b.createdAt - a.createdAt;
    });
}

export function relevantHomeDocs(
  docs: Doc[],
  relevantProjectIds: Set<string>,
  currentUserId: string | null
) {
  return [...docs].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    const aProject = a.projectId && relevantProjectIds.has(a.projectId) ? 0 : 1;
    const bProject = b.projectId && relevantProjectIds.has(b.projectId) ? 0 : 1;
    if (aProject !== bProject) return aProject - bProject;
    const aAuthored = currentUserId && a.authorId === currentUserId ? 0 : 1;
    const bAuthored = currentUserId && b.authorId === currentUserId ? 0 : 1;
    if (aAuthored !== bAuthored) return aAuthored - bAuthored;
    return b.updatedAt - a.updatedAt;
  });
}
